import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import ScrollFadeIn from '../components/ScrollFadeIn';
import {
  Mic,
  Bot,
  PieChart,
  Eye,
  ShieldCheck,
  Users,
  Check,
  Video,
  BookOpen,
  Brain,
  GraduationCap,
} from 'lucide-react';

const SocraPage: React.FC = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroEyebrow><Mic /> AI-POWERED ORAL &amp; TEXT ASSESSMENT</HeroEyebrow>
          <HeroTitle>
            질문은 더 깊게,<br />
            평가는 더 분명하게.
          </HeroTitle>
          <ProductWordmark>SOCRATEST</ProductWordmark>
          <HeroTagline>교재 근거 안에서 이어지는 AI 구술·텍스트 평가</HeroTagline>
          <HeroDescription>
            교수자가 확정한 평가목표를 따라 학생의 답을 한 단계 더 묻습니다.
            응시 후에는 답변 근거와 불확실성이 함께 담긴 채점 초안을 제공하고,
            최종 평가는 담당 교원이 검토·확정합니다.
          </HeroDescription>
          <HeroBadgeRow>
            <HeroBadge>평가목표 기반 출제</HeroBadge>
            <HeroBadge>출처 기반 후속 질문</HeroBadge>
            <HeroBadge>구술·텍스트 응시</HeroBadge>
            <HeroBadge>근거 기반 채점</HeroBadge>
            <HeroBadge>교수 검토·확정</HeroBadge>
          </HeroBadgeRow>
          <HeroActions>
            <HeroPrimaryLink href="https://socratest.freakit.co.kr/" target="_blank" rel="noopener noreferrer">
              SOCRATEST 시작하기
            </HeroPrimaryLink>
            <HeroTextLink href="#how-it-works">평가 방식 살펴보기</HeroTextLink>
          </HeroActions>
        </HeroContent>
      </HeroSection>

      {/* ── 교수/학생 기능 탭 ── */}
      <RoleSection>
        <SectionTitle>핵심 기능</SectionTitle>
        <RoleTabs>
          <ScrollFadeIn direction="right" delay={0.1}>
            <RoleColumn>
              <RoleLabel>교수용</RoleLabel>
              <FeatureList>
                <FeatureListItem>
                  <FeatureListIcon><BookOpen /></FeatureListIcon>
                  <FeatureListText>
                    <strong>평가목표 기반 출제 설계</strong><br />
                    교재와 문항을 바탕으로 평가목표·중요도·필수 여부·질문 수를 설계하고 AI 조교 추천을 선택 적용
                  </FeatureListText>
                </FeatureListItem>
                <FeatureListItem>
                  <FeatureListIcon><Brain /></FeatureListIcon>
                  <FeatureListText>
                    <strong>근거 자료 관리</strong><br />
                    PDF의 텍스트와 도표를 분석해 출제와 후속 질문에 사용할 교재 근거를 준비
                  </FeatureListText>
                </FeatureListItem>
                <FeatureListItem>
                  <FeatureListIcon><PieChart /></FeatureListIcon>
                  <FeatureListText>
                    <strong>교수 운영 대시보드</strong><br />
                    오늘 할 일, 시험 세트·문항별 응시와 채점 현황, 재접속 차단과 채점 실패를 한눈에 확인
                  </FeatureListText>
                </FeatureListItem>
                <FeatureListItem>
                  <FeatureListIcon><Users /></FeatureListIcon>
                  <FeatureListText>
                    <strong>학생·그룹 관리</strong><br />
                    개별·CSV 학생 등록, 그룹 구성과 시험 배정 지원
                  </FeatureListText>
                </FeatureListItem>
                <FeatureListItem>
                  <FeatureListIcon><GraduationCap /></FeatureListIcon>
                  <FeatureListText>
                    <strong>근거 기반 AI 채점 초안</strong><br />
                    평가목표별 내용 점수와 구술 전달력 참고 지표, 저신뢰 결과를 제시하고 교수 검토·확정 지원
                  </FeatureListText>
                </FeatureListItem>
                <FeatureListItem>
                  <FeatureListIcon><Video /></FeatureListIcon>
                  <FeatureListText>
                    <strong>통합 검토·리포트</strong><br />
                    문답과 채점 근거, 학업 리포트, 화면·웹캠 원본과 무결성 사건을 분리해 검토
                  </FeatureListText>
                </FeatureListItem>
              </FeatureList>
            </RoleColumn>
          </ScrollFadeIn>

          <ScrollFadeIn direction="left" delay={0.2}>
            <RoleColumn>
              <RoleLabel>학생용</RoleLabel>
              <FeatureList>
                <FeatureListItem>
                  <FeatureListIcon><Mic /></FeatureListIcon>
                  <FeatureListText>
                    <strong>구술·텍스트 응시</strong><br />
                    시험별 입력 방식에 따라 버튼 녹음 또는 직접 입력으로 답변
                  </FeatureListText>
                </FeatureListItem>
                <FeatureListItem>
                  <FeatureListIcon><Bot /></FeatureListIcon>
                  <FeatureListText>
                    <strong>STT 답변 확인</strong><br />
                    구술 답변의 Whisper 전사본을 제한 시간 안에 확인·정정하고 수정 이력을 투명하게 기록
                  </FeatureListText>
                </FeatureListItem>
                <FeatureListItem>
                  <FeatureListIcon><Brain /></FeatureListIcon>
                  <FeatureListText>
                    <strong>범위 안 후속 질문</strong><br />
                    직전 답변에 맞춰 교수가 정한 시험 범위와 교재 근거 안에서 한 번에 하나씩 심층 질문
                  </FeatureListText>
                </FeatureListItem>
                <FeatureListItem>
                  <FeatureListIcon><Video /></FeatureListIcon>
                  <FeatureListText>
                    <strong>화면·웹캠 원본 기록</strong><br />
                    화면공유와 웹캠 원본, 전체화면·포커스·업로드 상태를 교사 검토용으로 안전하게 보존
                  </FeatureListText>
                </FeatureListItem>
                <FeatureListItem>
                  <FeatureListIcon><Eye /></FeatureListIcon>
                  <FeatureListText>
                    <strong>단계별 응시 흐름</strong><br />
                    장치 점검부터 답변·녹화 업로드·세트 종료까지 지금 할 행동 하나를 명확히 안내
                  </FeatureListText>
                </FeatureListItem>
              </FeatureList>
            </RoleColumn>
          </ScrollFadeIn>
        </RoleTabs>
      </RoleSection>

      {/* ── 작동 원리 ── */}
      <PipelineSection id="how-it-works">
        <SectionTitle>작동 원리</SectionTitle>
        <PipelineSubtitle>
          답변 입력부터 검증된 꼬리 질문 출제까지, 모든 과정이 사실 기반으로 통제됩니다.
        </PipelineSubtitle>
        <PipelineFlow>
          {[
            { step: '01', title: '평가 설계 확정', desc: '교수가 평가목표·배점·질문 흐름과 범위를 확정해 시험 스냅샷으로 저장' },
            { step: '02', title: '답변 입력', desc: '구술 모드는 버튼 녹음과 Whisper STT, 텍스트 모드는 직접 입력으로 제출' },
            { step: '03', title: '상태·근거 분석', desc: '직전 답변의 학습자 상태를 판단하고 현재 섹션의 교재 근거를 검색' },
            { step: '04', title: '후속 질문 검증', desc: '평가목표와 자료 범위 안의 단일 질문인지 검증한 뒤 다음 질문을 출제' },
            { step: '05', title: '채점 초안과 검토', desc: '응시 후 근거·불확실성을 포함한 점수 초안을 만들고 교수가 최종 확정' },
          ].map((p, i) => (
            <ScrollFadeIn key={p.step} direction="up" delay={0.1 * (i + 1)}>
              <PipelineCard>
                <PipelineStep>{p.step}</PipelineStep>
                <PipelineCardTitle>{p.title}</PipelineCardTitle>
                <PipelineCardDesc>{p.desc}</PipelineCardDesc>
              </PipelineCard>
            </ScrollFadeIn>
          ))}
        </PipelineFlow>
      </PipelineSection>

      {/* ── 핵심 기술 심층 ── */}
      <DeepTechSection>
        <SectionTitle>SOCRATEST를 특별하게 만드는 기술</SectionTitle>
        <DeepTechGrid>
          <ScrollFadeIn direction="up" delay={0.1}>
            <DeepTechCard>
              <DeepTechIcon><Brain /></DeepTechIcon>
              <DeepTechTitle>평가목표를 따라가는 질문 흐름</DeepTechTitle>
              <DeepTechDesc>
                교수자가 확정한 <strong>평가목표·중요도·필수 여부·질문 수 예산</strong>에 따라
                질문 단계를 구성합니다. 직전 답변의 상태에 맞추되, 한 번에 한 가지 생각만 묻도록 통제합니다.
              </DeepTechDesc>
            </DeepTechCard>
          </ScrollFadeIn>
          <ScrollFadeIn direction="up" delay={0.2}>
            <DeepTechCard>
              <DeepTechIcon><BookOpen /></DeepTechIcon>
              <DeepTechTitle>범위를 벗어나지 않는 근거 검색</DeepTechTitle>
              <DeepTechDesc>
                PDF의 텍스트와 도표를 분석하고 의미·키워드 검색을 결합합니다.
                후속 질문은 <strong>교수의 시험 범위와 현재 섹션의 교재 근거</strong>가 겹치는 영역으로 제한합니다.
              </DeepTechDesc>
            </DeepTechCard>
          </ScrollFadeIn>
          <ScrollFadeIn direction="up" delay={0.3}>
            <DeepTechCard>
              <DeepTechIcon><ShieldCheck /></DeepTechIcon>
              <DeepTechTitle>판정이 아닌 무결성 검토 보조</DeepTechTitle>
              <DeepTechDesc>
                화면·웹캠 원본과 전체화면·포커스·녹화 상태를 객관적 사건으로 보존합니다.
                기록은 교사가 맥락과 함께 검토하며 <strong>부정행위 자동 판정이나 자동 감점에 사용하지 않습니다.</strong>
              </DeepTechDesc>
            </DeepTechCard>
          </ScrollFadeIn>
          <ScrollFadeIn direction="up" delay={0.4}>
            <DeepTechCard>
              <DeepTechIcon><GraduationCap /></DeepTechIcon>
              <DeepTechTitle>설명 가능한 채점과 인간 감독</DeepTechTitle>
              <DeepTechDesc>
                평가목표별 정확성·완전성·추론을 답변 근거와 함께 제시하고, 구술 전달력은 별도 참고 지표로 제공합니다.
                <strong>저신뢰·평가 불가 결과를 표시</strong>하며 최종 점수는 담당 교원이 검토·확정합니다.
              </DeepTechDesc>
            </DeepTechCard>
          </ScrollFadeIn>
        </DeepTechGrid>
      </DeepTechSection>

      {/* ── 도입 사례 ── */}
      <AdoptionSection>
        <AdoptionContent>
          <AdoptionEyebrow>도입 사례</AdoptionEyebrow>
          <AdoptionTitle>KAIST 강의 현장에 도입되었습니다</AdoptionTitle>
          <AdoptionDesc>
            2025년 2학기 KAIST 인지신경과학 정규 수업에 SOCRATEST가 시범 도입되어,
            실제 대학 강의 환경에서 AI 기반 구술 평가의 효용성을 검증했습니다.
          </AdoptionDesc>
          <AdoptionLink to="/achievements">FreakIT의 성과 보기 →</AdoptionLink>
        </AdoptionContent>
      </AdoptionSection>

      {/* ── 왜 SOCRATEST? ── */}
      <BenefitsSection>
        <BenefitsContent>
          <BenefitsText>
            <SectionTitle>왜 SOCRATEST인가?</SectionTitle>
            <BenefitsList>
              <ScrollFadeIn direction="up" delay={0.1}>
                <BenefitItem>
                  <BenefitIcon><Check /></BenefitIcon>
                  <BenefitText>
                    <BenefitTitle>교수자 중심의 AI 출제 설계</BenefitTitle>
                    <BenefitDescription>
                      AI는 평가목표와 질문 후보를 제안하고, 교수자가 선택·수정·확정한 설계만 실제 시험에 사용합니다.
                    </BenefitDescription>
                  </BenefitText>
                </BenefitItem>
              </ScrollFadeIn>
              <ScrollFadeIn direction="up" delay={0.2}>
                <BenefitItem>
                  <BenefitIcon><Check /></BenefitIcon>
                  <BenefitText>
                    <BenefitTitle>근거가 남는 적응형 평가</BenefitTitle>
                    <BenefitDescription>
                      학생의 답변에 맞춰 깊이를 조절하면서도 질문 범위와 출처, 전체 문답을 함께 보존합니다.
                    </BenefitDescription>
                  </BenefitText>
                </BenefitItem>
              </ScrollFadeIn>
              <ScrollFadeIn direction="up" delay={0.3}>
                <BenefitItem>
                  <BenefitIcon><Check /></BenefitIcon>
                  <BenefitText>
                    <BenefitTitle>검토 가능한 AI 채점</BenefitTitle>
                    <BenefitDescription>
                      점수만 보여주지 않고 평가목표별 근거와 불확실성을 함께 제공해 교수의 최종 판단을 지원합니다.
                    </BenefitDescription>
                  </BenefitText>
                </BenefitItem>
              </ScrollFadeIn>
              <ScrollFadeIn direction="up" delay={0.4}>
                <BenefitItem>
                  <BenefitIcon><Check /></BenefitIcon>
                  <BenefitText>
                    <BenefitTitle>평가와 무결성 기록의 분리</BenefitTitle>
                    <BenefitDescription>
                      학업 채점과 응시 무결성 기록을 분리해, 브라우저 사건이나 녹화 문제로 점수가 자동 변경되지 않습니다.
                    </BenefitDescription>
                  </BenefitText>
                </BenefitItem>
              </ScrollFadeIn>
            </BenefitsList>
          </BenefitsText>
        </BenefitsContent>
      </BenefitsSection>

      {/* ── 활용 사례 ── */}
      <UseCaseSection>
        <SectionTitle>활용 사례</SectionTitle>
        <UseCaseGrid>
          <UseCaseCard>
            <UseCaseTitle>대학·정규 수업 평가</UseCaseTitle>
            <UseCaseDescription>
              교재와 강의 목표에 맞춘 구술·서술 평가로 학생의 개념 이해와 추론 과정을 깊이 확인합니다.
            </UseCaseDescription>
          </UseCaseCard>
          <UseCaseCard>
            <UseCaseTitle>학교·학원 심층 평가</UseCaseTitle>
            <UseCaseDescription>
              정답형 시험만으로 보기 어려운 설명 능력과 오개념을 후속 질문으로 확인하고 근거 중심 피드백을 제공합니다.
            </UseCaseDescription>
          </UseCaseCard>
          <UseCaseCard>
            <UseCaseTitle>대규모 동시 평가</UseCaseTitle>
            <UseCaseDescription>
              학생·그룹 배정, 시험 세트, 일괄 채점과 통계를 통해 여러 학급의 평가를 일관된 기준으로 운영합니다.
            </UseCaseDescription>
          </UseCaseCard>
        </UseCaseGrid>
      </UseCaseSection>

      <CTASection>
        <CTAContent>
          <CTATitle>SOCRATEST로 시작하세요</CTATitle>
          <CTADescription>
            지금 바로 SOCRATEST를 체험하고 AI 기반 구술 평가의
            혁신을 경험해보세요
          </CTADescription>
          <CTAButtons>
            <PrimaryButton href="https://socratest.freakit.co.kr/" target="_blank" rel="noopener noreferrer">
              SOCRATEST 바로가기
            </PrimaryButton>
            <SecondaryButton to="/contact">
              문의하기
            </SecondaryButton>
          </CTAButtons>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
};

export default SocraPage;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled.div`
  padding-top: 80px;
`;
const HeroSection = styled.section`
  min-height: 760px;
  padding: 7.5rem 2rem 6.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  &::before {
    content: '';
    position: absolute;
    width: min(760px, 82vw);
    height: min(760px, 82vw);
    border: 1px solid rgba(127, 176, 212, 0.08);
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: auto;
    padding: 5.5rem 1.25rem 4.5rem;
  }
`;
const HeroContent = styled.div`
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  animation: ${fadeIn} 0.9s ease-out;
`;
const HeroEyebrow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  color: ${({ theme }) => theme.colors.accent};
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.17em;
  margin-bottom: 1.6rem;

svg { font-size: 1.05rem; }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: 290px;
    line-height: 1.65;
    justify-content: center;

    svg { display: none; }
  }
`;
const HeroTitle = styled.h1`
  max-width: 960px;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: clamp(3rem, 6.8vw, 5.7rem);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.045em;
  text-wrap: balance;
  word-break: keep-all;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.65rem;
    line-height: 1.16;
  }
`;
const ProductWordmark = styled.p`
  margin: 0.8rem 0 1.6rem;
  color: ${({ theme }) => theme.colors.accent};
  font-size: clamp(3rem, 6.5vw, 5.2rem);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.035em;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin: 1rem 0 1.5rem;
    font-size: 2.8rem;
  }
`;
const HeroTagline = styled.p`
  color: #dbeafe;
  font-size: clamp(1.12rem, 2.3vw, 1.45rem);
  font-weight: 700;
  line-height: 1.55;
  margin-bottom: 1.7rem;
`;
const HeroDescription = styled.p`
  max-width: 800px;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.08rem;
  line-height: 1.95;
  text-wrap: pretty;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    line-height: 1.85;
  }
`;
const HeroBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem 1.25rem;
  justify-content: center;
  margin-top: 2.25rem;
`;
const HeroBadge = styled.span`
  color: #a9cce4;
  font-size: 0.9rem;
  font-weight: 650;
  line-height: 1.5;
  white-space: nowrap;

  &::before {
    content: '—';
    margin-right: 0.55rem;
    color: rgba(127, 176, 212, 0.55);
  }
`;
const HeroActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1.35rem;
  margin-top: 2.75rem;
`;
const HeroPrimaryLink = styled.a`
  padding: 0.95rem 1.6rem;
  background: #f8fafc;
  color: #020617;
  border-radius: 8px;
  font-weight: 800;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: #dbeafe;
    transform: translateY(-2px);
  }
`;
const HeroTextLink = styled.a`
  padding: 0.6rem 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.38);
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

/* ── Pipeline ── */
const PipelineSection = styled.section`
  padding: 6rem 2rem;
  background: transparent;
  scroll-margin-top: 90px;
`;
const PipelineSubtitle = styled.p`
  max-width: 760px;
  margin: -1.4rem auto 3.5rem;
  text-align: center;
  font-size: 1.08rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const PipelineFlow = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.16);

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;
const PipelineCard = styled.div`
  height: 100%;
  padding: 1.7rem 1.4rem 1.5rem;
  border-right: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.6rem 0.25rem;
    border-right: 0;
  }
`;
const PipelineStep = styled.div`
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 1rem;
`;
const PipelineCardTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.75rem;
`;
const PipelineCardDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ── Deep Tech ── */
const DeepTechSection = styled.section`
  padding: 6rem 2rem;
  background: rgba(15, 23, 42, 0.18);
  border-block: 1px solid ${({ theme }) => theme.colors.border};
`;
const DeepTechGrid = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 4rem;
  row-gap: 1.25rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;
const DeepTechCard = styled.div`
  height: 100%;
  padding: 1.8rem 0 2.2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.17);
`;
const DeepTechIcon = styled.div`
  font-size: 1.55rem;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 1.25rem;
`;
const DeepTechTitle = styled.h3`
  font-size: 1.42rem;
  font-weight: 700;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.9rem;
`;
const DeepTechDesc = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: ${({ theme }) => theme.colors.textSecondary};
  strong {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 700;
  }
`;

/* ── Adoption ── */
const AdoptionSection = styled.section`
  padding: 4rem 2rem;
  background: transparent;
`;
const AdoptionContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;
const AdoptionEyebrow = styled.p`
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 1rem;
`;
const AdoptionTitle = styled.h2`
  font-size: 1.9rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1.25rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.6rem;
  }
`;
const AdoptionDesc = styled.p`
  font-size: 1.15rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
`;
const AdoptionLink = styled(Link)`
  display: inline-block;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: ${({ theme }) => theme.transitions.fast};
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

/* ── Role Section ── */
const RoleSection = styled.section`
  padding: 6rem 2rem;
  background: transparent;
`;
const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 2.6rem);
  font-weight: 800;
  line-height: 1.3;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 3.2rem;
`;
const RoleTabs = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 3.5rem;
  }
`;
const RoleColumn = styled.div`
  display: flex;
  flex-direction: column;
`;
const RoleLabel = styled.h3`
  font-size: 1.45rem;
  font-weight: 750;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textPrimary};
  padding-bottom: 1rem;
  border-bottom: 2px solid ${({ theme }) => theme.colors.accent};
  margin-bottom: 0;
`;
const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
`;
const FeatureListItem = styled.div`
  display: flex;
  gap: 1.2rem;
  align-items: flex-start;
  padding: 1.55rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
`;
const FeatureListIcon = styled.div`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.accent};
  flex-shrink: 0;
  margin-top: 0.15rem;
`;
const FeatureListText = styled.p`
  font-size: 0.98rem;
  line-height: 1.82;
  color: ${({ theme }) => theme.colors.textSecondary};
  strong {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-size: 1.05rem;
    line-height: 1.45;
    font-weight: 700;
    display: block;
    margin-bottom: 0.28rem;
  }
`;

/* ── Benefits ── */
const BenefitsSection = styled.section`
  padding: 6rem 2rem;
  background: transparent;
`;
const BenefitsContent = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;
const BenefitsText = styled.div``;
const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
`;
const BenefitItem = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  padding: 1.7rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
`;
const BenefitIcon = styled.div`
  color: ${({ theme }) => theme.colors.accent};
  font-size: 1.35rem;
  flex-shrink: 0;
  margin-top: 0.2rem;
`;
const BenefitText = styled.div`
  flex: 1;
`;
const BenefitTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.55rem;
`;
const BenefitDescription = styled.p`
  font-size: 1rem;
  line-height: 1.82;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ── Use Cases ── */
const UseCaseSection = styled.section`
  padding: 6rem 2rem;
  background: rgba(15, 23, 42, 0.18);
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;
const UseCaseGrid = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;
const UseCaseCard = styled.div`
  padding: 0.35rem 0 0.35rem 1.5rem;
  border-left: 2px solid rgba(127, 176, 212, 0.55);
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const UseCaseTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.45;
  margin-bottom: 0.8rem;
`;
const UseCaseDescription = styled.p`
  font-size: 1rem;
  line-height: 1.82;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ── CTA ── */
const CTASection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;
const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const CTATitle = styled.h2`
  font-size: 2.1rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.9rem;
  }
`;
const CTADescription = styled.p`
  font-size: 1.5rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.25rem;
  }
`;
const CTAButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
`;
const PrimaryButton = styled.a`
  display: inline-block;
  padding: 0.9rem 2rem;
  background: #ffffff;
  color: #020617;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1.2rem;
  border: none;
  transition: ${({ theme }) => theme.transitions.cubic};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  }
`;
const SecondaryButton = styled(Link)`
  display: inline-block;
  padding: 0.9rem 2rem;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1.2rem;
  backdrop-filter: blur(10px);
  transition: ${({ theme }) => theme.transitions.cubic};
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-4px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
`;

/* end of file */